import dayjs, { Dayjs } from 'dayjs';
import { BookingStepsContextState } from '../../context/bookingContext/bookingContext.types';
import {
  BookingStepValueEnum,
  BookingSubStepValueEnum,
} from '../../components/BookingWidget/bookingSteps.interface';
import {
  BookingQuoteBeneficiaries,
  BookingQuoteBeneficiariesEnum,
} from '../../components/BookingWidget/BookingQuoteManagment/bookingQuote.interface';
import {
  FormQuotationInformationEnum,
  FormQuotationInformationTraveller,
  FormQuotationInformationTravellerEnum,
  FormQuotationInformationTravellerList,
} from '../../components/QuotationInformationForm/quoation-information.interface';
import { getBirthdateForTraveller } from './getTravellersAge';
import { ApiOutputDateFormat } from '../consts/app.const';
import { Companion } from '../../components/BookingWidget/BookingManagment/bookingManagment.interface';
import { TravellerAgeAndBirthdate } from '../../components/QuotationFormWeb/quotationFormWeb.types';

const ageString = 'travellerAge';
const firstNameString = 'travellerFirstName';
const lastNameString = 'travellerLastName';

export const syncCompanionsWithTravellersAge = (
  companionsToSync: Companion[],
  travellersAgeToSync: TravellerAgeAndBirthdate[],
): Companion[] =>
  companionsToSync.filter((companion) =>
    travellersAgeToSync.some(
      (traveller) => traveller.companionId === companion.id,
    ),
  );

export const getBeneficiariesArrayFromTravellersAgeArray = (
  travellersAge: TravellerAgeAndBirthdate[],
  companions: Companion[] | undefined = undefined,
  options: {
    inferDateOfBirth?: boolean;
    skipAge?: boolean;
    includeMedicalId?: boolean;
  } = {
    inferDateOfBirth: false,
    skipAge: false,
    includeMedicalId: false,
  },
): BookingQuoteBeneficiaries[] => {
  const { inferDateOfBirth, skipAge, includeMedicalId } = options;
  return travellersAge.map((t, i) => {
    const dob =
      getBirthdateForTraveller(t.dateOfBirth as Dayjs) ||
      (inferDateOfBirth
        ? getBirthdateForTraveller(travellersAge[i]?.age)
        : null);
    return {
      ...(companions &&
        companions[i]?.id && {
          [BookingQuoteBeneficiariesEnum.IdNumber]: companions[i].id,
        }),
      ...(includeMedicalId &&
        companions &&
        companions[i]?.medicalId && {
          [BookingQuoteBeneficiariesEnum.MedicalId]: companions[i].medicalId,
        }),
      ...(!skipAge && {
        [BookingQuoteBeneficiariesEnum.Age]: Number(t.age),
      }),
      ...(dob && {
        birthDate: dob,
      }),
    };
  });
};

const getBeneficiariesArray = (
  bookingStepsContext: BookingStepsContextState,
  companions: Companion[] | undefined = undefined,
  options: {
    inferDateOfBirth?: boolean;
    skipAge?: boolean;
  } = {
    inferDateOfBirth: false,
    skipAge: false,
  },
): BookingQuoteBeneficiaries[] => {
  const { inferDateOfBirth, skipAge } = options;
  const quotationInformation =
    bookingStepsContext[BookingStepValueEnum.QuotationInformation];
  const quotationForm = bookingStepsContext[BookingStepValueEnum.QuotationForm];
  const travellersList =
    quotationInformation[BookingSubStepValueEnum.InformationTravellers];
  const informationPersonal =
    quotationInformation[BookingSubStepValueEnum.InformationPersonal];
  const travellersAge = quotationForm[BookingSubStepValueEnum.TravellersAge];
  const isPolicyHolderTravellingValue =
    bookingStepsContext?.[BookingStepValueEnum.QuotationInformation]?.[
      BookingSubStepValueEnum.IsPolicyHolderTravelling
    ];

  let policyHolderIndex = -1;
  if (Array.isArray(isPolicyHolderTravellingValue)) {
    policyHolderIndex = isPolicyHolderTravellingValue.findIndex(
      (isPolicyHolderTravelling) => isPolicyHolderTravelling,
    );
  } else if (isPolicyHolderTravellingValue === true) {
    policyHolderIndex = 0;
  }

  const syncedCompanions = syncCompanionsWithTravellersAge(
    companions || [],
    travellersAge,
  );

  if (
    (!informationPersonal ||
      !informationPersonal[FormQuotationInformationEnum.Birthdate]) &&
    travellersList === null
  ) {
    return getBeneficiariesArrayFromTravellersAgeArray(
      travellersAge,
      syncedCompanions,
      {
        inferDateOfBirth,
        skipAge,
      },
    );
  }
  const beneficiaries: BookingQuoteBeneficiaries[] = [];
  const getFieldValueOrDefault = (
    fieldType:
      | typeof firstNameString
      | typeof lastNameString
      | typeof ageString,
    index: number,
  ): string => {
    const fieldKey: keyof FormQuotationInformationTravellerList =
      `${fieldType}${index}` as keyof FormQuotationInformationTravellerList;
    return travellersList[fieldKey] || '';
  };

  let i = 0;
  while (i < 11) {
    const keyAge: string = `${ageString}${i}`.toString();
    if (
      travellersList[keyAge as keyof FormQuotationInformationTravellerList] &&
      travellersAge[i]
    ) {
      let age = null;
      const { dateOfBirth } = travellersAge[i] || {};
      let birthDate =
        dateOfBirth && dayjs(dateOfBirth).isValid()
          ? dateOfBirth.format(ApiOutputDateFormat)
          : null;
      if (!inferDateOfBirth && !birthDate && travellersAge[i].age) {
        age = Number(travellersAge[i].age);
      }
      if (inferDateOfBirth && !birthDate) {
        birthDate = getBirthdateForTraveller(
          travellersList[
            `${ageString}${i}` as keyof FormQuotationInformationTravellerList
          ],
        );
      }

      beneficiaries.push({
        ...(birthDate && {
          [BookingQuoteBeneficiariesEnum.BirthDate]: birthDate,
        }),
        // allow 0 as valid value
        ...(age !== undefined &&
          age !== null && {
            [BookingQuoteBeneficiariesEnum.Age]: Number(travellersAge[i].age),
          }),
        [BookingQuoteBeneficiariesEnum.Firstname]: getFieldValueOrDefault(
          firstNameString,
          i,
        ),
        [BookingQuoteBeneficiariesEnum.Lastname]: getFieldValueOrDefault(
          lastNameString,
          i,
        ),
        [BookingQuoteBeneficiariesEnum.Subscriber]: policyHolderIndex === i,
        ...(travellersList[
          `travellerId${i}` as keyof FormQuotationInformationTravellerList
        ] && {
          [BookingQuoteBeneficiariesEnum.IdNumber]: Number(
            travellersList[
              `travellerId${i}` as keyof FormQuotationInformationTravellerList
            ],
          ),
        }),
      });
    } else if (travellersAge[i]) {
      const { companionId, dateOfBirth, age } = travellersAge[i];
      let birthDate = getBirthdateForTraveller(dateOfBirth as Dayjs) || null;
      if (inferDateOfBirth && !birthDate) {
        birthDate = getBirthdateForTraveller(age);
      }
      beneficiaries.push({
        ...(birthDate && {
          [BookingQuoteBeneficiariesEnum.BirthDate]: birthDate,
        }),
        ...(!skipAge && {
          [BookingQuoteBeneficiariesEnum.Age]: Number(age),
        }),
        ...(companionId && {
          [BookingQuoteBeneficiariesEnum.IdNumber]: companionId,
        }),
      });
    }
    // eslint-disable-next-line no-plusplus
    i++;
  }
  return beneficiaries;
};

export const getBeneficiariesListFromTravellersArray = (
  travelers: FormQuotationInformationTraveller[],
  travellerList: FormQuotationInformationTravellerList,
  policyHolderIsTraveller: boolean,
): FormQuotationInformationTravellerList => {
  const newTravellerList: FormQuotationInformationTravellerList = {};
  travelers.forEach(
    (traveller: FormQuotationInformationTraveller, index: number) => {
      // Only process if we have valid date
      if (
        traveller[FormQuotationInformationTravellerEnum.BirthDate] &&
        !dayjs(
          traveller[FormQuotationInformationTravellerEnum.BirthDate],
        ).isValid()
      ) {
        return; // Skip invalid dates
      }
      const travellerIndex = policyHolderIsTraveller ? index + 1 : index;
      newTravellerList[
        `travellerId${travellerIndex}` as keyof FormQuotationInformationTravellerList
      ] = traveller[FormQuotationInformationTravellerEnum.Id]?.toString() || '';
      newTravellerList[
        `travellerFirstName${travellerIndex}` as keyof FormQuotationInformationTravellerList
      ] = traveller[FormQuotationInformationTravellerEnum.Firstname];
      newTravellerList[
        `travellerLastName${travellerIndex}` as keyof FormQuotationInformationTravellerList
      ] = traveller[FormQuotationInformationTravellerEnum.Lastname];
      if (traveller[FormQuotationInformationTravellerEnum.BirthDate]) {
        newTravellerList[
          `travellerAge${travellerIndex}` as keyof FormQuotationInformationTravellerList
        ] = traveller[FormQuotationInformationTravellerEnum.BirthDate];
      } else {
        newTravellerList[
          `travellerAge${travellerIndex}` as keyof FormQuotationInformationTravellerList
        ] = traveller[FormQuotationInformationTravellerEnum.Age];
      }
    },
  );
  return { ...travellerList, ...newTravellerList };
};

export default getBeneficiariesArray;
